import history from 'app/history';
import { getPageContentFromCms } from 'app/modules/cms/service/cms-service';
import { associativeCmsMap } from 'app/modules/cms/tools/mapper';
import { initializeInsuredGoods } from 'app/modules/corporate/service/corporate.service';
import { Accessibility } from 'app/modules/corporate/service/store/corporate.state';
import { saveCustomer } from 'app/modules/customer/service/customer.service';
import { gtmEvent } from 'app/modules/gtm/service/gtm.service';
import { createPendingSubscription, getSubscription, initializeJourney } from 'app/modules/subscription/service/subscription-service';
import { isNull } from 'lodash';
import { journeyApi } from '../api';
import { associativeGtmStepNameMap, associativeStepMap } from '../ui/tools/mappers';
import { returnComponent } from '../ui/tools/methods';
import { ACTION_TYPES } from './store/step.actions';
import { StepActions } from './store/step.state';
import { isEmptyArray, isEmptyObject } from 'app/shared/util/method-util';

export const majCmsContent = () => async (dispatch, getState) => {
  const stepStore = await getState().step;
  const currentPageType = stepStore.journeySteps[stepStore.journeyStepsList[stepStore.currentStep - 1]].cmsType;

  if (currentPageType !== undefined) {
    const cmsStore = await getState().cms;
    let cmsContentPageType = cmsStore.content[currentPageType];

    if (cmsContentPageType === undefined) {
      await dispatch(getPageContentFromCms(currentPageType));
    }

    cmsContentPageType = await getState().cms.content[currentPageType];
  }
};

export const buildSteps = () => async (dispatch, getState) => {
  const { isLoaded } = await getState().step;
  if (isLoaded) return; // si déjà loaded, stop

  const { selectedOffer } = await getState().offers;

  const offerAccessibility = selectedOffer?.accessibility;

  const { steps, pendingSubscription } = await getState().config;

  let journeyStepsList = [];

  switch (offerAccessibility) {
    case Accessibility.PROFESSIONAL:
      journeyStepsList = [...steps.pro];
      break;
    case Accessibility.INDIVIDUAL:
      journeyStepsList = [...steps.part];
      break;
    default:
      journeyStepsList = [...steps.part];
      break;
  }

  const journeySteps: { [id: string]: any } = {};
  journeyStepsList.map((element, i) => {
    const step = {
      component: returnComponent(associativeStepMap.get(element)),
      cmsType: associativeCmsMap.get(element),
      showStepActions: true
    };

    journeySteps[element] = step;
  });

  // Add first step
  if (offerAccessibility) {
    journeyStepsList.unshift('confirmOfferStep');
  }

  if (pendingSubscription) {
    journeyStepsList.push('confirmationBarcodeStep');
  } else {
    journeyStepsList.push('confirmationStep');
  }

  journeyStepsList.push('paymentConfirmationStep');
  journeyStepsList.push('paymentFailureStep');

  journeyStepsList.push('cancellationStep');

  journeyStepsList.push('tooLateStep');

  journeyStepsList.push('tooEarlyStep');

  await dispatch({
    type: ACTION_TYPES.SET_JOURNEY_STEPS,
    payload: { journeySteps, journeyStepsList }
  });
};

export const setStepActions = ({
  actionBeforePreviousStep,
  actionBeforeNextStep,
  actionNextStep,
  previousButtonLabel,
  nextButtonLabel,
  showPreviousButton,
  showNextButton,
  disableButtons,
  disablePreviousButton,
  disableNextButton,
  form,
  fullSize
}: StepActions) => async (dispatch, getState) => {
  await dispatch({
    type: ACTION_TYPES.SET_STEPS_ACTIONS,
    payload: {
      actionBeforePreviousStep,
      actionBeforeNextStep,
      actionNextStep,
      previousButtonLabel,
      nextButtonLabel,
      showPreviousButton,
      showNextButton,
      disableButtons,
      disablePreviousButton,
      disableNextButton,
      form,
      fullSize
    }
  });
};

export const disableButton = () => async (dispatch, getState) => {
  await dispatch({
    type: ACTION_TYPES.DISABLE_BUTTONS,
    payload: {
      disableButtons: true
    }
  });
};

export const disablePreviousBtn = () => async dispatch => {
  await dispatch({
    type: ACTION_TYPES.DISABLE_PREVIOUS_BUTTON,
    payload: {
      disablePreviousButton: true
    }
  });
};

export const disableNextBtn = () => async dispatch => {
  await dispatch({
    type: ACTION_TYPES.DISABLE_NEXT_BUTTON,
    payload: {
      disableNextButton: true
    }
  });
};

export const initStep = () => (dispatch, getState) => {
  dispatch(majCmsContent());
};

export const goToStep = (stepName: string) => async (dispatch, getState) => {
  const { journeyStepsList, currentStep } = await getState().step;
  await dispatch({
    type: ACTION_TYPES.UPDATE_STEP,
    payload: { previousStep: currentStep, currentStep: journeyStepsList.indexOf(stepName) + 1 }
  });
};

export const editStep = (stepName: string) => async dispatch => {
  await dispatch(gtmEvent('Edition', associativeGtmStepNameMap.get(stepName), '0'));

  dispatch(goToStep(stepName));
};

// TODO: faire le bon appel
export const actionSummaryNextStep = (reCaptchaValue?) => async (dispatch, getState) => {
  const { subscriptionNumber, offers } = await getState().subscription;
  const { selectedOffer } = await getState().offers;
  const { insuredGood } = await getState().insuredElementData;
  const { recaptchaActivated, recaptchaSiteKey } = await getState().config;
  const customer = await getState().customer;

  let prospection = {};
  if (!isEmptyArray(offers)) {
    prospection = { ...prospection, offers };
  } else {
    prospection = { ...prospection, offers: [selectedOffer?.code] };
  }

  if (!isEmptyObject(insuredGood)) {
    prospection = {
      ...prospection,
      insuredGood,
      subscriber: { ...customer }
    };
  }

  let success;

  if (subscriptionNumber) {
    success = await dispatch(saveCustomer(subscriptionNumber, customer));
  } else if (recaptchaActivated && recaptchaSiteKey !== '') {
    success = await dispatch(createPendingSubscription(prospection, reCaptchaValue));
  } else {
    success = await dispatch(createPendingSubscription(prospection));
  }

  if (!success) return;

  const { currentStep } = await getState().step;

  await dispatch({ type: ACTION_TYPES.DISABLE_BUTTONS, payload: { disableButtons: true } });
  await dispatch({ type: ACTION_TYPES.UPDATE_STEP, payload: { previousStep: currentStep, currentStep: currentStep + 1 } });

  dispatch(majCmsContent());
};

export const initializeHome = (numberSubscription: string, shortOfferCode: string) => async (dispatch, getState) => {
  if (numberSubscription !== undefined) {
    await dispatch(buildSteps());

    // const signed = await subscriptionApi.requestSubscriptionSignStatus(numberSubscription)
    await dispatch(getSubscription(numberSubscription));
    const subscription = await getState().subscription;
    const { currentStep, journeyStepsList } = await getState().step;
    const currentStepName = journeyStepsList[currentStep - 1];
    if (currentStepName !== 'cancellationStep' && currentStepName !== 'paymentFailureStep') {
      if (
        (subscription.status === 'SIGNED' || subscription.status === 'PAYMENT_IN_PROGRESS') &&
        subscription.paymentMethod === 'ONE_SHOT_PAYMENT'
      )
        await dispatch(goToStep('paymentStep'));
      else if (subscription.status === 'PAYMENT_DONE' || subscription.status === 'PAYMENT_VALIDATED')
        await dispatch(goToStep('paymentConfirmationStep'));
      else if (!isNull(subscription.signTxnCompletedAt)) await dispatch(goToStep('confirmationStep'));
      else if (isNull(subscription.signTxnCompletedAt) && subscription.status !== 'PENDING') await dispatch(goToStep('tooLateStep'));
      else if (!isNull(subscription.legalEndDateReflection) && subscription.legalEndDateReflection > new Date())
        await dispatch(goToStep('tooEarlyStep'));
    }
  } else {
    await dispatch(initializeJourney(shortOfferCode));
    await dispatch(buildSteps());
  }

  await dispatch({ type: ACTION_TYPES.SET_STEPS_LOADED });
};

export const initializeCallback = (stepName: string, subscriptionNumber: string) => async (dispatch, getState) => {
  await dispatch(buildSteps());

  // sign (true si signé et false si non signé)
  // const signed = await subscriptionApi.requestSubscriptionSignStatus(subscriptionNumber)
  await dispatch(getSubscription(subscriptionNumber));
  const subscription = await getState().subscription;

  // Si !payment || documents signés mais non payés
  // TODO: modifier le libellé du statut
  // TODO: faire une enum
  // TODO: faire un reload plutôt qu'un replace ?

  // TODO: POS : supprimer quand les status existeront...
  if (stepName !== 'paymentFailureStep') {
    if (stepName !== 'paymentConfirmationStep' && (subscription.status === 'PAYMENT_DONE' || subscription.status === 'PAYMENT_VALIDATED')) {
      history.replace('/' + subscriptionNumber + '/payment-done');
      return;
    } else if (stepName !== 'paymentStep' && (subscription.status === 'SIGNED' || subscription.status === 'PAYMENT_IN_PROGRESS')) {
      history.replace('/' + subscriptionNumber + '/payment');
      return;
    } else if (stepName === 'confirmationStep' && subscription.subscriptionNumber && isNull(subscription.signTxnCompletedAt)) {
      // Si confirmation && les documents ne sont pas signés > error
      history.replace('/' + subscriptionNumber + '/failure?error=documentsnotsigned');
      return;
    } else if (stepName === 'confirmationStep' && !subscription.subscriptionNumber) {
      history.replace('/' + subscriptionNumber + '/failure');
      return;
    }
  }

  // sinon affiche l'étape
  await dispatch(goToStep(stepName));

  history.replace('/' + subscriptionNumber);
};

export const goNextStep = () => async (dispatch, getState) => {
  const { disableButtons, disableNextButton, actionBeforeNextStep, currentStep, actionNextStep, journeyStepsList } = getState().step;

  if (journeyStepsList[currentStep - 1] === 'cancellationStep' || journeyStepsList[currentStep - 1] === 'confirmationStep') return;

  if (disableButtons || disableNextButton) return;

  await dispatch({ type: ACTION_TYPES.DISABLE_BUTTONS, payload: { disableButtons: true } });

  if (actionBeforeNextStep !== null && actionBeforeNextStep !== undefined && !(await actionBeforeNextStep())) {
    await dispatch({ type: ACTION_TYPES.DISABLE_BUTTONS, payload: { disableButtons: false } });
    return;
  }

  if (actionNextStep !== null && actionNextStep !== undefined) {
    await actionNextStep();
    return;
  }

  await dispatch({ type: ACTION_TYPES.UPDATE_STEP, payload: { previousStep: currentStep, currentStep: currentStep + 1 } });

  dispatch(majCmsContent());

  await dispatch(gtmEvent(associativeGtmStepNameMap.get(journeyStepsList[currentStep - 1]), 'Suivant', '0'));
};

export const goPreviousStep = () => async (dispatch, getState) => {
  const { disableButtons, disablePreviousButton, actionBeforePreviousStep, currentStep, journeyStepsList } = getState().step;

  if (currentStep === 1 || disableButtons || disablePreviousButton) return;

  dispatch({ type: ACTION_TYPES.DISABLE_BUTTONS, payload: { disableButtons: true } });

  if (actionBeforePreviousStep !== null && actionBeforePreviousStep !== undefined) await actionBeforePreviousStep();

  if (currentStep > 1) {
    dispatch({ type: ACTION_TYPES.UPDATE_STEP, payload: { previousStep: currentStep - 2, currentStep: currentStep - 1 } }); // TODO : check utility previousStep
  }

  await dispatch(gtmEvent(associativeGtmStepNameMap.get(journeyStepsList[currentStep - 1]), 'Précédent', '0'));
};

export const checkIban = async (iban: string) => {
  const response = await journeyApi.checkIban(iban);
  return response.data;
};

export const initProductsStep = () => dispatch => {
  dispatch(initializeInsuredGoods());
};

export const getCheckSum = async (subscriptionNumber: number, shortOfferCode: string) => {
  const result = await journeyApi.getCheckSum(subscriptionNumber, shortOfferCode);

  return result.data;
};
